'use client';

import { useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import { AlertCircle } from 'lucide-react';
import { Alert, AlertDescription } from '@/components/ui/alert';
import type { MatterFile } from '@/lib/services/files/types';

export interface EditDocumentDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  document: MatterFile;
  onSave: (updates: { isCertifiedCopy: boolean }) => void;
}

export function EditDocumentDialog({
  open,
  onOpenChange,
  document,
  onSave
}: EditDocumentDialogProps) {
  const [isCertifiedCopy, setIsCertifiedCopy] = useState(
    document.isCertifiedCopy
  );

  const handleSave = () => {
    onSave({ isCertifiedCopy });
    onOpenChange(false);
  };

  const displayName = document.displayName || document.originalFilename;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Edit Document</DialogTitle>
          <DialogDescription>Update document details</DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          {/* Document Info */}
          <div>
            <p className="mb-1 text-sm font-medium">Document</p>
            <p className="text-muted-foreground text-sm">{displayName}</p>
          </div>

          {/* Backend Limitation Notice */}
          <Alert>
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>
              Currently, only the certified copy status can be updated. Document
              renaming will be available in a future update.
            </AlertDescription>
          </Alert>

          {/* Certified Copy Toggle */}
          <div className="space-y-4">
            <div className="flex items-center space-x-2">
              <Checkbox
                id="certified-edit"
                checked={isCertifiedCopy}
                onCheckedChange={(checked) =>
                  setIsCertifiedCopy(checked as boolean)
                }
              />
              <Label
                htmlFor="certified-edit"
                className="text-sm leading-none font-medium peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
              >
                This is a certified copy
              </Label>
            </div>
            <p className="text-muted-foreground text-xs">
              Certified copies are official documents from authoritative sources
            </p>
          </div>
        </div>

        <DialogFooter>
          <Button
            size="xs"
            density="compact"
            variant="outline"
            onClick={() => onOpenChange(false)}
          >
            Cancel
          </Button>
          <Button size="xs" density="compact" onClick={handleSave}>
            Save Changes
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
